import { useState } from 'react';
import {
  BatteryFullIcon,
  Info,
  LaptopMinimalCheckIcon,
  SmartphoneIcon,
} from 'lucide-react';
import classNames from 'classnames';
import i18n from '@/settings/i18n';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import DefaultOption from './options/Default';
import ConditionOption from './options/Condition';
import ColorOption from './options/Color';
import KeyboardOption from './options/Keyboard';
import ConditionModal from './condition/ConditionModal';
import settings from '@/settings/index';
import Checkpoints from '../../checkpoints';
import BatteryHealthUpgrade from './BatteryHealthUpgrade';
import TradeInOption from '@/components/tradein/TradeInOption';
import ScreenSizeOption from './options/ScreenSize';
import { KEYBOARD_SLUG, isVariantShown } from './helpers';
import {
  getCanonicalMiniCondition,
  getMiniConditionDepartment,
  getMiniConditionParts,
  MiniConditionPart,
} from './miniConditionInfo';

const t = i18n.init('product');

// Categories that keep the original Screen/Body/Battery mini description. Every
// other category is served from `miniConditionInfo` (keyed by department), so this
// map must stay byte-for-byte unchanged to preserve existing electronics copy.
const TECH_DEVICE_CATEGORIES = [
  'Laptops',
  'Smartwatches',
  'Tablets',
  'Smartphones',
  // French translations
  'Montres connectées',
  'Tablettes',
  'Ordinateurs portables',
  // spanish translations
  'Portátiles',
  // italian translations
  'Laptop',
];

const conditionInfo: Record<string, MiniConditionPart[]> = {
  Good: [
    {
      title: 'Screen',
      icon: LaptopMinimalCheckIcon,
      text: 'Micro-scratches (invisible when on)',
    },
    {
      title: 'Body',
      icon: SmartphoneIcon,
      text: 'Visible scratches',
    },
    {
      title: 'Battery',
      icon: BatteryFullIcon,
      text: 'Tested for reliable daily use',
    },
  ],
  'Very Good': [
    {
      title: 'Screen',
      icon: LaptopMinimalCheckIcon,
      text: 'Light micro-scratches (invisible when on)',
    },
    {
      title: 'Body',
      icon: SmartphoneIcon,
      text: 'Light scratches',
    },
    {
      title: 'Battery',
      icon: BatteryFullIcon,
      text: 'Tested for reliable daily use',
    },
  ],
  'Like New': [
    {
      title: 'Screen',
      icon: LaptopMinimalCheckIcon,
      text: 'Pristine (no scratches)',
    },
    {
      title: 'Body',
      icon: SmartphoneIcon,
      text: 'Minimal to no wear',
    },
    {
      title: 'Battery',
      icon: BatteryFullIcon,
      text: 'Tested for reliable daily use',
    },
  ],
};

const OPTION_STYLE =
  'text-xxs sm:text-xs w-[calc(50%-6px)] xxs:w-[calc((100%/3)-6px)] min-w-fit sm:w-[calc(25%-6px)] last:pr-0';

const colorSlugs = ['color', 'watchCaseColor', 'watchBandColor'];

export const generateVariantNameLabel = (name: string, handle: string) => {
  if (
    handle.toLowerCase().includes('color') &&
    settings.store === 'reebelo-uk'
  ) {
    return name.replace('Color', 'Colour');
  }

  return t(name);
};

const VariantName = ({
  variantName,
  isColorVariant,
  selectedOption,
  variantSlug,
  selectedOptionDisplayValue,
}: {
  variantName: string;
  isColorVariant: boolean;
  selectedOption: string;
  variantSlug: string;
  selectedOptionDisplayValue?: string;
}) => {
  const {
    selectedSkuDetails: { categoryId },
  } = useProductPageContext();
  const [showConditionModal, setShowConditionModal] = useState(false);

  // Hide battery health variant title, as we are using the BatteryHealthUpgrade component instead
  if (variantSlug === 'batteryHealth') {
    return null;
  }

  const label = generateVariantNameLabel(variantName, variantSlug);

  return (
    <>
      <div className="flex items-center gap-x-1 px-2">
        <h6 className="text-xxs font-medium uppercase">
          {label}
          {isColorVariant && selectedOption && (
            <>
              :{' '}
              <span className="font-bold">
                {selectedOptionDisplayValue || selectedOption}
              </span>
            </>
          )}
        </h6>
        {/* Condition Info */}
        {variantSlug === 'condition' && categoryId && (
          <button
            aria-label="Condition Info"
            className="shrink-0 focus:outline-none"
            onClick={() => {
              setShowConditionModal(true);
            }}
          >
            <Info className="-mt-0.5 h-3 w-3" />
          </button>
        )}
      </div>
      {/* Condition Info Modal */}
      {variantSlug === 'condition' && (
        <ConditionModal
          showModal={showConditionModal}
          setShowModal={setShowConditionModal}
          activeCondition={selectedOption}
        />
      )}
    </>
  );
};

const Variants = () => {
  const {
    product: { variants },
    selectedVariant,
    variantTree,
    selectedSkuDetails: { categoryName },
    additionalInfo: { screenSizeOptions, breadcrumb },
  } = useProductPageContext();

  if (!selectedVariant) {
    return null;
  }

  const hasScreenSizes = (screenSizeOptions?.length ?? 0) > 0;

  // Tech devices keep the original Screen/Body/Battery map; every other category
  // is resolved from its department (the PDP breadcrumb) via `miniConditionInfo`.
  const isTechDevice = TECH_DEVICE_CATEGORIES.includes(categoryName);
  const miniConditionDepartment = getMiniConditionDepartment(breadcrumb);

  return (
    <>
      <Checkpoints className="px-5 py-2 lg:hidden" />
      <div className="mb-2 px-3 lg:rounded-md lg:border lg:px-2 lg:py-4">
        {hasScreenSizes && (
          <div id="e2e-pdp-screenSize">
            <VariantName
              variantSlug="screenSize"
              variantName={t`Screen Size`}
              isColorVariant={false}
              selectedOption=""
            />
            <div className="flex flex-wrap items-end gap-2 px-2">
              {screenSizeOptions?.map((option) => (
                <ScreenSizeOption
                  key={option.slug}
                  option={option}
                  className={OPTION_STYLE}
                />
              ))}
            </div>
          </div>
        )}
        {variants && variants.length > 0 && (
          <>
            {variants.map((v, index) => {
              const currentVariantName = v.variant.name;
              const currentVariantSlug = v.variant.slug;

              const isColorVariant = colorSlugs.includes(currentVariantSlug);
              const selectedVariantOption =
                selectedVariant.variants.find(
                  (variant) => variant.variant.slug === currentVariantSlug,
                )?.value?.value ?? '';
              const selectedVariantDisplayValue =
                selectedVariant.variants.find(
                  (variant) => variant.variant.slug === currentVariantSlug,
                )?.value?.displayValue ?? undefined;
              const canonicalCondition = getCanonicalMiniCondition(
                selectedVariantOption,
                settings.store,
              );

              let conditionInfoForValue: MiniConditionPart[] = [];

              if (currentVariantSlug === 'condition') {
                conditionInfoForValue = isTechDevice
                  ? conditionInfo[canonicalCondition] ?? []
                  : getMiniConditionParts(
                      miniConditionDepartment,
                      categoryName,
                      selectedVariantOption,
                      settings.store,
                    );
              }

              const shouldShowConditionInfo =
                currentVariantSlug === 'condition' &&
                conditionInfoForValue.length > 0;

              if (
                isVariantShown(
                  currentVariantSlug,
                  v.values.length,
                  settings.store,
                )
              ) {
                return (
                  <div
                    key={index}
                    className={classNames({
                      'mt-4': currentVariantSlug !== 'batteryHealth',
                      'first:mt-0':
                        currentVariantSlug !== 'batteryHealth' &&
                        !hasScreenSizes,
                    })}
                    id={`e2e-pdp-${v.variant.slug}`}
                  >
                    <VariantName
                      variantSlug={currentVariantSlug}
                      variantName={currentVariantName}
                      isColorVariant={isColorVariant}
                      selectedOption={selectedVariantOption}
                      selectedOptionDisplayValue={selectedVariantDisplayValue}
                    />
                    {currentVariantSlug === KEYBOARD_SLUG ? (
                      <div className="px-2">
                        <KeyboardOption
                          variant={v}
                          variantSlug={currentVariantSlug}
                          selectedOption={selectedVariantOption}
                        />
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          'flex flex-wrap items-end gap-2 px-2',
                          {
                            'relative rounded-md p-2 pt-2 shadow-[0_2px_4px_0_rgba(14,_78,_50,_0.10)]':
                              shouldShowConditionInfo,
                          },
                        )}
                      >
                        {v.values.map((value, valueIndex) => {
                          const currentVariantValue = value.value;

                          const isSelected = selectedVariant.variants.some(
                            (sku) =>
                              sku.variant.slug === currentVariantSlug &&
                              sku.value.value === currentVariantValue,
                          );

                          // Next Selection
                          const nextSelection = [
                            {
                              variant: v.variant,
                              value,
                            },
                          ];

                          // Next Selection Offer
                          const offerVariant = variantTree.get(
                            selectedVariant.variants,
                            nextSelection,
                          );

                          if (offerVariant.shouldHide) {
                            return null;
                          }

                          // Deprecated: We're using BatteryHealthUpgrade Component Instead
                          if (currentVariantSlug === 'batteryHealth') {
                            return null;
                          }

                          if (isColorVariant) {
                            return (
                              <ColorOption
                                key={valueIndex}
                                isSelected={isSelected}
                                offer={offerVariant}
                                value={value}
                                hexValue={value.hexValue}
                                variantSlug={currentVariantSlug}
                              />
                            );
                          }

                          if (currentVariantSlug === 'condition') {
                            return (
                              <ConditionOption
                                key={valueIndex}
                                isSelected={isSelected}
                                offer={offerVariant}
                                value={value}
                                className={OPTION_STYLE}
                                variantSlug={currentVariantSlug}
                              />
                            );
                          }

                          return (
                            <DefaultOption
                              key={valueIndex}
                              isSelected={isSelected}
                              offer={offerVariant}
                              value={value}
                              className={OPTION_STYLE}
                              variantSlug={currentVariantSlug}
                            />
                          );
                        })}
                      </div>
                    )}
                    {shouldShowConditionInfo && (
                      <div className="mx-auto flex w-[98%] flex-col gap-2 rounded-b-xl bg-gray-300 p-2 xl:flex-row xl:flex-wrap xl:items-center">
                        {conditionInfoForValue.map((info, infoIndex) => (
                          <div
                            key={`condition-info-${infoIndex}`}
                            className="flex items-center gap-x-1 self-start text-[10px] xl:self-center"
                          >
                            <info.icon
                              className="h-3 w-3 shrink-0"
                              strokeWidth={2.25}
                            />
                            <div className="leading-tight">
                              {info.title && (
                                <span className="font-medium">
                                  {t(info.title)}:{' '}
                                </span>
                              )}
                              {t(info.text)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {v.variant.slug === 'batteryHealth' && (
                      <BatteryHealthUpgrade />
                    )}
                  </div>
                );
              }

              return null;
            })}
          </>
        )}
        {/* Purchase flow only. */}
        <TradeInOption />
      </div>
    </>
  );
};

export default Variants;
